/**
 * en.ts — Canonical English string dictionary.
 *
 * This file is the single source of truth for translation keys. Every other
 * locale is typed against `Dictionary` (derived from this object), so a missing
 * or misspelled key becomes a compile-time error.
 *
 * Schema: keys are fully hierarchical and semantic — nested by
 * component → region/element → property (e.g.
 * `memberDirectory.editModal.saveAndClose`). Avoid generic, flat keys; place
 * each string under the specific UI surface it belongs to so ownership and
 * context are unambiguous. Add new strings here first.
 *
 * Interpolation: use `{name}` placeholders; pass params to `t(key, { name })`.
 */
export const en = {
    searchBox: {
        placeholder: 'Enter a search term',
        clearLabel: 'Clear',
    },
    resultsMeta: {
        summary: 'Found {count:number} results in {seconds} seconds',
    },
    pagination: {
        navLabel: 'Search results pagination',
        previous: 'Previous',
        next: 'Next',
    },
    facetPanel: {
        heading: 'Refiners',
        removeLabel: 'Remove',
        clear: 'Clear Refiners',
    },
    viewSwitcher: {
        cards: 'Cards',
        grid: 'Grid',
        table: 'Table',
    },
    userToolbar: {
        add: 'Add Users',
        email: 'Email',
        copy: 'Copy',
        export: 'Export',
        addModal: {
            title: 'Add Users',
            closeLabel: 'Close',
            tabExisting: 'Find Existing',
            tabNew: 'Review & Add Users',
            tabOutlook: 'Paste from Outlook',
            tabExcel: 'Paste from Excel',
            searchPlaceholder: 'Search for users…',
            firstName: 'First name',
            lastName: 'Last name',
            email: 'Email',
            addRow: 'Add row',
            removeRow: 'Remove row',
            outlookHint: 'Paste names/addresses copied from Outlook (semicolon or newline separated).',
            excelHint: 'Paste rows from Excel: First name, Last name, Email (tab separated).',
            queueSummary: '{count:number} user(s) queued for review.',
            reviewQueue: 'Review queue ({count:number})',
            addToQueueReview: 'Add to Queue & Review ({count:number})',
            addToQueue: 'Add to queue ({count:number})',
            addingToQueue: 'Adding {count:number} user(s) to queue…',
            addedToQueue: '{count:number} user(s) added to the queue.',
            reviewSubmit: 'Add {count:number} user(s) to group',
            processing: 'Processing {current:number} of {total:number}…',
            cancel: 'Cancel',
            close: 'Close',
            submit: 'Add',
            discard: {
                title: 'Discard queued users?',
                body: 'You have {count:number} user(s) queued for review that have not been added yet. Closing now will discard them. Are you sure you want to close?',
                confirm: 'Discard & Close',
                cancel: 'Keep Editing',
            },
            status: {
                column: 'Status',
                checking: 'Checking…',
                invite: 'Invite user',
                addToGroup: 'Add to group',
                alreadyInGroup: 'Already in group',
                inviting: 'Inviting…',
                adding: 'Adding…',
                invited: 'Invited',
                added: 'Added',
                error: 'Error',
            },
            validation: {
                required: 'This field is required.',
                invalidEmail: 'Enter a valid email address.',
            },
        },
        search: {
            searching: 'Searching…',
            noResults: 'No matching users.',
            failed: 'Search failed.',
            add: 'Queue',
            queued: 'Queued',
            added: 'Added',
            queueHint: 'Add {name} to the review queue',
        },
        toast: {
            loadMembersFailed: 'Unable to load group members.',
            noEmails: 'No email addresses available.',
            copySucceeded: 'Copied {count:number} address(es) to the clipboard.',
            copyFailed: 'Unable to copy to the clipboard.',
            exportEmpty: 'No users were returned to export.',
            exportSucceeded: 'User export downloaded.',
            exportFailed: 'Failed to export users.',
            userAdded: 'User added to group.',
            userQueued: 'User queued for review.',
            addUserFailed: 'Failed to add user to group.',
            noUsersToAdd: 'No users to add.',
            usersAdded: 'Added {count:number} user(s).',
            addUsersFailed: 'Failed to add users.',
            usersAddedPartial: 'Added {added:number} of {total:number}; {failed:number} failed. Review the highlighted rows.',
        },
        exportFilename: 'User Export - {stamp}',
    },
    searchTable: {
        search: {
            loading: {
                status: 'Loading...',
                message: 'Searching...',
            },
        },
        results: {
            empty: {
                message: 'No results found. Please try another search term.',
                messageForTerm: 'No results found for "{term}". Please try another search term.',
            },
        },
        export: {
            excel: 'Export to Excel',
            pdf: 'Export to PDF',
        },
    },
    memberDirectory: {
        search: {
            loading: {
                status: 'Loading...',
                message: 'Searching...',
            },
        },
        facets: {
            jobTitle: 'Job Title',
            department: 'Department',
        },
        fields: {
            displayName: 'Name',
            jobTitle: 'Job Title',
            companyName: 'Company',
            department: 'Department',
            mail: 'Email',
            chat: 'Chat',
            businessPhones: 'Phone',
            mobilePhone: 'Mobile',
            linkedIn: 'LinkedIn',
            twitter: 'Twitter',
            youTube: 'YouTube',
            blog: 'Blog',
            companyWebsite: 'Website',
            streetAddress: 'Address',
            city: 'City',
            state: 'State/Province',
            postalCode: 'Postal Code',
            country: 'Country',
        },
        results: {
            empty: {
                message: 'No results found. Please try another search term.',
                messageForTerm: 'No results found for "{term}". Please try another search term.',
            },
        },
        detailsModal: {
            closeLabel: 'Close',
            contactHeading: 'Contact Information',
            viewProfile: 'View profile',
            moreDetails: 'More details',
            fewerDetails: 'Fewer details',
            editUser: 'Edit user',
            removeUser: 'Remove user',
        },
        editModal: {
            closeLabel: 'Close',
            loadingStatus: 'Loading...',
            heading: 'Edit User',
            headingNamed: 'Edit User: {name}',
            changePhoto: 'Change photo',
            removeFromGroup: 'Remove from group',
            save: 'Save',
            saveAndClose: 'Save & Close',
            toggleDropdown: 'Toggle Dropdown',
            close: 'Close',
        },
        confirmModal: {
            title: 'Confirm',
            closeLabel: 'Close',
            message: 'Remove {name} from the current group?',
            fallbackName: 'this user',
            cancel: 'Cancel',
            confirm: 'Remove',
        },
        toast: {
            loadUserFailed: 'Unable to load user details.',
            readImageFailed: 'Unable to read the selected image.',
            noChanges: 'No changes to save.',
            updateSucceeded: 'User updated successfully.',
            updateFailed: 'Failed to update user.',
            removeSucceeded: 'User removed from group.',
            removeFailed: 'Failed to remove user from group.',
        },
    },
    dataroom: {
        shared: {
            loading: 'Loading...',
            close: 'Close',
            notAvailable: 'n/a',
        },
        documents: {
            heading: 'Documents',
            toolbar: {
                upload: 'Upload documents',
                update: 'Update document metadata',
                download: 'Download all documents',
                excel: 'Open Data Room Configuration Excel',
                sharePoint: 'Open in SharePoint',
            },
            details: {
                title: 'Document Details',
                open: 'Open',
                documentType: 'Document Type',
                comments: 'Comments',
                instructions: 'Instructions',
                lastModified: 'Last Modified',
            },
            upload: {
                title: 'Upload Documents',
                dropFiles: 'Drop files to upload',
                or: 'or',
                selectFiles: 'Select Files',
                addFiles: 'Add Files',
                maxSize: 'Maximum upload file size: 2GB',
                submit: 'Upload',
                cancel: 'Cancel',
            },
            update: {
                title: 'Update Document Metadata',
                submit: 'Update',
                cancel: 'Cancel',
            },
            status: {
                uploadTitle: 'Upload in Progress',
                uploadMessage: 'Your upload is in progress. Please wait...',
                updateTitle: 'Update in Progress',
                updateMessage: 'Your update is in progress. Please wait...',
                downloadTitle: 'Download in Progress',
                downloadMessage: 'Your download is in progress. Please wait...',
                cancel: 'Cancel',
            },
            confirm: {
                title: 'Confirm',
                cancelUpload: 'Are you sure you want to cancel this upload?',
                cancelDownload: 'Are you sure you want to cancel this download?',
                no: 'No',
                yes: 'Yes',
            },
            grid: {
                deleteRow: 'Delete this row',
                fieldRequired: '{field} is required',
            },
            columns: {
                document: 'Document',
                lastModified: 'Last Modified',
            },
            toast: {
                uploadSucceeded: '{count:number} Document(s) Successfully Uploaded - {names}',
                uploadFailed: '{count:number} Document(s) Not Uploaded - {names}',
                editSucceeded: '{count:number} Document(s) Successfully Edited - {names}',
                editFailed: '{count:number} Document(s) Not Edited - {names}',
                downloadAborted: 'Download of {filename} aborted',
                downloadCompleted: 'Download of {filename} completed',
            },
        },
        properties: {
            heading: 'Data Room Properties',
            empty: 'No properties found',
        },
        requested: {
            heading: 'Requested Documents',
            columns: {
                documentType: 'Document Type',
                required: 'Required',
                instructions: 'Instructions',
            },
        },
        directory: {
            tooltip: 'Create Data Room',
            formModalTitle: 'Create New Data Room',
            create: 'Create',
            close: 'Close',
            statusModalTitle: 'Creating New Data Room',
            statusLoading: 'Loading...',
            statusMessage: 'Creating Data Room. Please wait...',
            toastSuccess: 'Data Room is being provisioned',
            toastError: 'An error occurred when trying to create data room',
        },
    },
} as const;

/** Widen literal leaf types (from `as const`) to `string` for locale files. */
type Widen<T> = {
    [K in keyof T]: T[K] extends string ? string : Widen<T[K]>;
};

export type Dictionary = Widen<typeof en>;
